import React, { useState } from "react";
import { AxiosResponse } from "axios";
import { FileSelector } from "./FileSelector";
import { ProgressIndicator } from "@fluentui/react/lib/ProgressIndicator";
import Button from "../Button";
import styled from "styled-components/macro";
import { hasItems } from "../../../utils/collections";
import { AnonymousCredential, newPipeline, BlockBlobClient } from "@azure/storage-blob";
import { platformActions } from "../../../communication/Communication";
import * as Actions from "../../../store/actions";
import { userFilesUrl, userImagesUrl } from "../../../constants/urls";
import format from "date-fns/format";
import { TransferProgressEvent } from "@azure/core-http/types/latest/src/webResource";

const maxFileSize = 2 * 1024 * 1024 * 1024; // 2GB

function getReadableFileSize(sizeInBytes: number): string {
  if (sizeInBytes >= 1024 * 1024 * 1024) {
    return `${Math.round(sizeInBytes / 1024 / 1024 / 1024)} GB`;
  } else if (sizeInBytes >= 1024 * 1024) {
    return `${Math.round(sizeInBytes / 1024 / 1024)} MB`;
  } else if (sizeInBytes >= 1024) {
    return `${Math.round(sizeInBytes / 1024)} KB`;
  } else {
    return `${sizeInBytes} bytes`;
  }
}

export const uploadFiles = async (
  nodeTrackingId: string | null,
  nodeAnswerHubId: number | null,
  files: File[],
  attachImage: boolean,   // attach as image or file
  onUploadFinished: (uploadedFileInfos: UploadedFileInfo[]) => void,
  onUploadFailed: (error: any) => void,
  onProgress?: (percentCompleted: number) => void,
  prefixTimestampToName?: boolean,
  approvedFileExtensions?: string,
): Promise<void> => {
  if (!nodeTrackingId && !nodeAnswerHubId) {
    onUploadFailed("Invalid tracking ID specified for upload");  // Shouldn't hit this in real but for testing
    return;
  }

  if (files.length === 0) {
    onUploadFailed("No file has been selected.");
    return;
  }

  if (files.length > 1) {
    onUploadFailed("Only one file can be uploaded at a time.");
    return;
  }

  const file = files[0];

  if (file.size > maxFileSize) {
    onUploadFailed(`This file can't be uploaded because it (${getReadableFileSize(file.size)}) exceeds the maximum size limit of ${getReadableFileSize(maxFileSize)}.`);
    return;
  }

  try {
    let destinationFileNamePart = sanitizeFileName(file.name);
    if (prefixTimestampToName) {
      destinationFileNamePart = `${format(new Date(), 'yyyyMMdd-HHmmss')}-${destinationFileNamePart}`;
    }

    // Get upload info
    const blobUploadRequest: BlobUploadRequestWithAHId = {
      requestVersion: 2,
      clientName: "Visual Studio Feedback Web", // Important, this will be used to identify file upload source in DB
      clientBuild: "",
      filename: destinationFileNamePart,
      filesize: file.size,
      trackingIdOfFeedback: nodeTrackingId,
      nodeId: nodeAnswerHubId,
    };

    const blobUploadRequestAPi = nodeTrackingId ? Actions.Apis.BlobUploadRequest : Actions.Apis.BlobUploadRequestByAHId;
    let response: AxiosResponse<BlobUploadInfo>;

    try {
      response = await platformActions.post(false, `${blobUploadRequestAPi}`, blobUploadRequest);
    } catch (error) {
      if (response?.status === 415 || error?.response?.status === 415 ||        // from web
        (error?.text && error.text.indexOf("415 UnsupportedMediaType") !== -1)  // from native process
      ) {
        onUploadFailed(`Your attachment must be one of: ${approvedFileExtensions}.`);
        return;
      }

      onUploadFailed("Unable to get file upload info.");
      return;
    }

    // Somehow when above call is made via native process the response doesn't contain any HTTP status code
    // So rely on presence of valid data fields
    if (!response.data.blobUploadLocation
      || !response.data.filename
      || !response.data.token) {
      if (response.status === 415) {
        onUploadFailed("This type of file is not allowed for uploading.");
        return;
      }

      onUploadFailed("Unable to get file upload info.");
      return;
    }

    const pipeline = newPipeline(new AnonymousCredential());
    const blockBlobURL = `${response.data.blobUploadLocation}/${response.data.filename}${response.data.token}`;
    const blobClient = new BlockBlobClient(blockBlobURL, pipeline);
    const uploadResponse = await blobClient.uploadData(file,
      {
        onProgress: (progress: TransferProgressEvent) => {
          onProgress?.(progress.loadedBytes / file.size * 100);
        }
      }
    );

    if (uploadResponse._response.status !== 200 && uploadResponse._response.status !== 201) {  // check all successfull codes
      onUploadFailed("Upload failed");
      return;
    }

    onProgress?.(100);

    const fileType = attachImage && file.type.startsWith("image") ? "image" : "other";

    onUploadFinished([{
      name: file.name,
      size: file.size,
      type: fileType,
      transferId: response.data.trackingId,
      url: `${fileType === "image" ? userImagesUrl : userFilesUrl}?name=${response.data.filename}&tid=${response.data.trackingId}`,
    } as UploadedFileInfo]);
  } catch (error) {
    console.log("Upload error: " + error);
    // reponse from native has code like this
    if (typeof error === "string" && error?.indexOf("ERR_SEND_API: 415") !== -1) {
      onUploadFailed("This type of file is not allowed for uploading.");
      return;
    }
    onUploadFailed(error);
  }
};

interface BlobUploadInfo {
  trackingId: string;
  blobUploadLocation: string;
  filename: string;
  token: string;
  expirationTimeUtc: Date;
  blocksUploaded: string[];
}

interface BlobUploadRequest {
  requestVersion: number;
  clientName: string;
  clientBuild: string;
  filename: string;
  filesize: number;
  trackingIdOfFeedback: string;
}

export interface BlobUploadRequestWithAHId extends BlobUploadRequest {
  nodeId: number;
}

export interface UploadedFileInfo {
  name: string;
  transferId: string;
  type: "image" | "other";
  size: number;
  url: string;
}

const sanitizeFileName = (fileName: string): string => {
  return encodeURI(fileName.replace(/\s/g, "_"));
}

export interface FileUploadProps {
  nodeTrackingId: string;
  nodeAnswerHubId?: number;
  attachImage: boolean;
  approvedFileExtensions?: string;

  onUploaded: (files: UploadedFileInfo[]) => void;
  onDismiss: () => void;
}

export default function FileUpload(props: FileUploadProps): JSX.Element {
  const [selectedFiles, setSelectedFiles] = useState<File[]>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>(null);
  const [percentComplete, setPercentComplete] = useState(0);

  const handleSubmit = async (e): Promise<void> => {
    e.preventDefault();
    await upload(selectedFiles);
  };

  const upload = async (files: File[]): Promise<void> => {
    setError(null);
    setIsUploading(true);

    await uploadFiles(
      props.nodeTrackingId,
      props.nodeAnswerHubId,
      files,
      props.attachImage,
      (uploadedFileInfos: UploadedFileInfo[]) => {
        setIsUploading(false);
        props.onUploaded(uploadedFileInfos)
      },
      (error: any) => {
        setIsUploading(false);
        setError(`Upload failed. ${error.message ?? error}`)
      },
      (percentCompleted: number) => setPercentComplete(percentCompleted),
      false,
      props.approvedFileExtensions,
    );
  };

  return (
    <Form onSubmit={handleSubmit}>
      <FileSelector
        files={selectedFiles}
        allowedTypes={props.attachImage ? "image/png,image/gif,image/jpeg" : null}
        invaidTypeErrorMessage={props.attachImage ? "Please choose an image of type png, gif or jpeg only" : null}
        onChange={(files) => {
          setSelectedFiles(files);
          setError(null);
        }}
      />
      {isUploading && (
        <ProgressIndicator
          barHeight={12}
          styles={{
            itemProgress: {
              paddingTop: 0
            },
            progressBar: {
              borderRadius: "6px"
            },
            progressTrack: {
              borderRadius: "6px"
            }
          }}
          percentComplete={percentComplete}
        />
      )}

      {error && <div className="error">{error}</div>}

      <Footer>
        <Button type="button" buttonType="normal" disabled={isUploading} onClick={props.onDismiss}>Cancel</Button>
        <Button type="submit" disabled={!hasItems(selectedFiles) || isUploading}>Upload</Button>
      </Footer>
    </Form>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;

  >* + *{
    margin-top: 2rem;
  }
`;

const Footer = styled.div`
    align-self: flex-end;

    >* + *{
    margin-left: 1.5rem;
  }
`;